<template>
  <div class="company-manager">
    <!-- Header -->
    <header class="manager-header">
      <div class="header-content">
        <div class="header-left">
          <h1>🏢 {{ t('companyManager.title') }}</h1>
          <p>{{ t('companyManager.subtitle') }}</p>
        </div>
        
        <div class="header-right">
          <button @click="showCreateModal = true" class="create-btn">
            ➕ {{ t('companyManager.addCompany') }}
          </button>
          <router-link to="/saas/dashboard" class="back-btn">
            🏠 {{ t('companyManager.dashboard') }}
          </router-link>
        </div>
      </div>
    </header>

    <!-- Filters and Search -->
    <div class="filters-section">
      <div class="filters-content">
        <div class="search-group">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('companyManager.searchPlaceholder')"
            class="search-input"
          />
        </div>
        
        <div class="filter-group">
          <select v-model="statusFilter" class="filter-select">
            <option value="">{{ t('companyManager.allStatus') }}</option>
            <option value="active">{{ t('companyManager.activeOnly') }}</option>
            <option value="inactive">{{ t('companyManager.inactiveOnly') }}</option>
          </select>
        </div>
        
        <div class="stats-group">
          <span class="stat-item">
            {{ t('companyManager.total') }}: {{ filteredCompanies.length }}
          </span>
          <span class="stat-item">
            {{ t('companyManager.active') }}: {{ activeCompaniesCount }}
          </span>
        </div>
      </div>
    </div>

    <!-- Companies List -->
    <main class="companies-main">
      <div v-if="loading" class="loading-state">
        <p>🔄 {{ t('companyManager.loadingCompanies') }}</p>
      </div>

      <div v-else-if="filteredCompanies.length === 0" class="empty-state">
        <div class="empty-content">
          <h3>🏢 {{ t('companyManager.noCompaniesFound') }}</h3>
          <p v-if="searchQuery">{{ t('companyManager.noMatchingCompanies') }}</p>
          <p v-else>{{ t('companyManager.getStartedMessage') }}</p>
          <button @click="showCreateModal = true" class="create-btn">
            ➕ {{ t('companyManager.createFirstCompany') }}
          </button>
        </div>
      </div>

      <div v-else class="companies-grid">
        <div 
          v-for="company in filteredCompanies" 
          :key="company.id"
          class="company-card"
        >
          <div class="company-header">
            <div class="company-title">
              <h3>{{ company.name }}</h3>
              <span :class="['status-badge', company.is_active ? 'active' : 'inactive']">
                {{ company.is_active ? `✅ ${t('companyManager.active')}` : `❌ ${t('companyManager.inactive')}` }}
              </span>
            </div>
            
            <div class="company-actions">
              <button 
                @click="editCompany(company)" 
                class="action-btn edit-btn"
                :title="t('companyManager.editCompany')"
              >
                ✏️
              </button>
              <button 
                @click="toggleCompanyStatus(company)" 
                class="action-btn toggle-btn"
                :title="company.is_active ? t('companyManager.deactivate') : t('companyManager.activate')"
              >
                {{ company.is_active ? '⏸️' : '▶️' }}
              </button>
              <button 
                @click="deleteCompany(company)" 
                class="action-btn delete-btn"
                :title="t('companyManager.deleteCompany')"
              >
                🗑️
              </button>
            </div>
          </div>

          <div class="company-details">
            <div class="detail-row">
              <span class="detail-label">{{ t('companyManager.schema') }}:</span>
              <span class="detail-value">{{ company.schema_name }}</span>
            </div>
            
            <div class="detail-row">
              <span class="detail-label">{{ t('companyManager.created') }}:</span>
              <span class="detail-value">{{ formatDate(company.created_at) }}</span>
            </div>
            
            <div class="detail-row" v-if="company.subdomain">
              <span class="detail-label">{{ t('companyManager.subdomain') }}:</span>
              <span class="detail-value">{{ company.subdomain }}</span>
            </div>
            
            <div class="detail-row" v-if="company.org_number">
              <span class="detail-label">{{ t('companyManager.orgNumber') }}:</span>
              <span class="detail-value">{{ company.org_number }}</span>
            </div>
            
            <div class="detail-row" v-if="company.contact_email">
              <span class="detail-label">{{ t('companyManager.contact') }}:</span>
              <span class="detail-value">{{ company.contact_email }}</span>
            </div>
            
            <div class="detail-row" v-if="company.address">
              <span class="detail-label">{{ t('companyManager.address') }}:</span>
              <span class="detail-value">{{ company.address }}</span>
            </div>
            
            <div class="detail-row">
              <span class="detail-label">{{ t('companyManager.billingStatus') }}:</span>
              <span class="detail-value">
                <span :class="['billing-status', company.billing_status]">
                  {{ company.billing_status?.toUpperCase() || 'TRIAL' }}
                </span>
              </span>
            </div>
            
            <div class="detail-row" v-if="company.price_per_user">
              <span class="detail-label">{{ t('companyManager.pricePerUser') }}:</span>
              <span class="detail-value">{{ company.price_per_user }} SEK</span>
            </div>
            
            <div class="detail-row" v-if="company.description">
              <span class="detail-label">{{ t('companyManager.description') }}:</span>
              <span class="detail-value">{{ company.description }}</span>
            </div>
          </div>

          <div class="company-footer">
            <div class="company-stats">
              <span class="stat-chip">{{ t('companyManager.id') }}: {{ company.id }}</span>
              <span class="stat-chip">{{ company.is_active ? t('companyManager.operational') : t('companyManager.suspended') }}</span>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Create/Edit Modal -->
    <div v-if="showCreateModal || editingCompany" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>{{ editingCompany ? `✏️ ${t('companyManager.editCompany')}` : `➕ ${t('companyManager.createCompany')}` }}</h2>
          <button @click="closeModal" class="modal-close">✖️</button>
        </div>

        <form @submit.prevent="saveCompany" class="modal-form">
          <div class="form-group">
            <label for="name">{{ t('companyManager.companyName') }} *</label>
            <input
              id="name"
              v-model="companyForm.name"
              type="text"
              required
              :placeholder="t('companyManager.enterCompanyName')"
              :disabled="formLoading"
            />
          </div>

          <div class="form-group">
            <label for="schema_name">{{ t('companyManager.schemaName') }} *</label>
            <input
              id="schema_name"
              v-model="companyForm.schema_name"
              type="text"
              required
              :placeholder="t('companyManager.schemaPlaceholder')"
              pattern="^[a-z][a-z0-9_]*$"
              :title="t('companyManager.schemaTitle')"
              :disabled="formLoading || editingCompany"
            />
            <small class="form-hint">{{ t('companyManager.schemaHint') }}</small>
          </div>

          <div class="form-group">
            <label for="contact_email">{{ t('companyManager.contactEmail') }}</label>
            <input
              id="contact_email"
              v-model="companyForm.contact_email"
              type="email"
              :placeholder="t('companyManager.contactEmailPlaceholder')"
              :disabled="formLoading"
            />
          </div>

          <div class="form-group">
            <label for="subdomain">{{ t('companyManager.subdomain') }}</label>
            <input
              id="subdomain"
              v-model="companyForm.subdomain"
              type="text"
              :placeholder="t('companyManager.subdomainPlaceholder')"
              :disabled="formLoading"
            />
            <small class="form-hint">{{ t('companyManager.subdomainHint') }}</small>
          </div>

          <div class="form-group">
            <label for="org_number">{{ t('companyManager.organizationNumber') }}</label>
            <input
              id="org_number"
              v-model="companyForm.org_number"
              type="text"
              :placeholder="t('companyManager.orgNumberPlaceholder')"
              :disabled="formLoading"
            />
          </div>

          <div class="form-group">
            <label for="address">{{ t('companyManager.address') }}</label>
            <textarea
              id="address"
              v-model="companyForm.address"
              :placeholder="t('companyManager.addressPlaceholder')"
              rows="2"
              :disabled="formLoading"
            ></textarea>
          </div>

          <div class="form-group">
            <label for="price_per_user">{{ t('companyManager.pricePerUserSEK') }}</label>
            <input
              id="price_per_user"
              v-model.number="companyForm.price_per_user"
              type="number"
              step="0.01"
              min="0"
              placeholder="39.00"
              :disabled="formLoading"
            />
          </div>

          <div class="form-group">
            <label for="billing_status">{{ t('companyManager.billingStatus') }}</label>
            <select
              id="billing_status"
              v-model="companyForm.billing_status"
              :disabled="formLoading"
            >
              <option value="trial">{{ t('companyManager.trial') }}</option>
              <option value="active">{{ t('companyManager.active') }}</option>
              <option value="suspended">{{ t('companyManager.suspended') }}</option>
            </select>
          </div>

          <div class="form-group">
            <label for="description">{{ t('companyManager.description') }}</label>
            <textarea
              id="description"
              v-model="companyForm.description"
              :placeholder="t('companyManager.descriptionPlaceholder')"
              rows="3"
              :disabled="formLoading"
            ></textarea>
          </div>

          <div class="form-section">
            <h4>{{ t('companyManager.overtimeRatesOptional') }}</h4>
            <div class="form-row">
              <div class="form-group">
                <label for="overtime_rate1">{{ t('companyManager.rate1') }}</label>
                <input
                  id="overtime_rate1"
                  v-model.number="companyForm.overtime_rate1"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  :disabled="formLoading"
                />
              </div>
              <div class="form-group">
                <label for="overtime_rate2">{{ t('companyManager.rate2') }}</label>
                <input
                  id="overtime_rate2"
                  v-model.number="companyForm.overtime_rate2"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  :disabled="formLoading"
                />
              </div>
              <div class="form-group">
                <label for="overtime_rate3">{{ t('companyManager.rate3') }}</label>
                <input
                  id="overtime_rate3"
                  v-model.number="companyForm.overtime_rate3"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  :disabled="formLoading"
                />
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input
                v-model="companyForm.is_active"
                type="checkbox"
                :disabled="formLoading"
              />
              <span class="checkmark">✓</span>
              {{ t('companyManager.activeCompany') }}
            </label>
          </div>

          <div class="modal-actions">
            <button type="button" @click="closeModal" class="cancel-btn" :disabled="formLoading">
              {{ t('companyManager.cancel') }}
            </button>
            <button type="submit" class="save-btn" :disabled="formLoading">
              {{ formLoading ? `⏳ ${t('companyManager.saving')}...` : (editingCompany ? t('companyManager.update') : t('companyManager.create')) }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deletingCompany" class="modal-overlay" @click="deletingCompany = null">
      <div class="modal-content danger-modal" @click.stop>
        <div class="modal-header">
          <h2>🗑️ {{ t('companyManager.deleteCompany') }}</h2>
          <button @click="deletingCompany = null" class="modal-close">✖️</button>
        </div>

        <div class="modal-body">
          <p>{{ t('companyManager.deleteConfirmation') }}</p>
          <p><strong>{{ deletingCompany.name }}</strong></p>
          <p class="warning-text">⚠️ {{ t('companyManager.deleteWarning') }}</p>
        </div>

        <div class="modal-actions">
          <button @click="deletingCompany = null" class="cancel-btn">
            {{ t('companyManager.cancel') }}
          </button>
          <button @click="confirmDelete" class="delete-btn" :disabled="formLoading">
            {{ formLoading ? `⏳ ${t('companyManager.deleting')}...` : t('companyManager.deleteCompany') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAdminStore } from '../../stores/admin.js'

const router = useRouter()
const { t } = useI18n()
const adminStore = useAdminStore()

// Reactive state
const loading = ref(false)
const formLoading = ref(false)
const searchQuery = ref('')
const statusFilter = ref('')
const showCreateModal = ref(false)
const editingCompany = ref(null)
const deletingCompany = ref(null)

const companyForm = ref({
  name: '',
  schema_name: '',
  subdomain: '',
  org_number: '',
  address: '',
  contact_email: '',
  description: '',
  is_active: true,
  billing_status: 'trial',
  price_per_user: 39.00,
  overtime_rate1: null,
  overtime_rate2: null,
  overtime_rate3: null
})

// Computed
const filteredCompanies = computed(() => {
  let companies = [...adminStore.companies]
  
  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    companies = companies.filter(company => 
      company.name.toLowerCase().includes(query) ||
      company.schema_name.toLowerCase().includes(query) ||
      (company.contact_email && company.contact_email.toLowerCase().includes(query))
    )
  }
  
  // Apply status filter
  if (statusFilter.value) {
    const isActive = statusFilter.value === 'active'
    companies = companies.filter(company => company.is_active === isActive)
  }
  
  return companies.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
})

const activeCompaniesCount = computed(() => 
  filteredCompanies.value.filter(c => c.is_active).length
)

// Methods
function formatDate(dateStr) {
  if (!dateStr) return 'N/A'
  
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return 'Invalid Date'
  
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

function resetForm() {
  companyForm.value = {
    name: '',
    schema_name: '',
    subdomain: '',
    org_number: '',
    address: '',
    contact_email: '',
    description: '',
    is_active: true,
    billing_status: 'trial',
    price_per_user: 39.00,
    overtime_rate1: null,
    overtime_rate2: null,
    overtime_rate3: null
  }
}

function closeModal() {
  showCreateModal.value = false
  editingCompany.value = null
  resetForm()
}

function editCompany(company) {
  editingCompany.value = company
  companyForm.value = {
    name: company.name,
    schema_name: company.schema_name,
    subdomain: company.subdomain || '',
    org_number: company.org_number || '',
    address: company.address || '',
    contact_email: company.contact_email || '',
    description: company.description || '',
    is_active: company.is_active,
    billing_status: company.billing_status || 'trial',
    price_per_user: company.price_per_user || 39.00,
    overtime_rate1: company.overtime_rate1 || null,
    overtime_rate2: company.overtime_rate2 || null,
    overtime_rate3: company.overtime_rate3 || null
  }
}

async function saveCompany() {
  if (formLoading.value) return
  
  formLoading.value = true
  
  try {
    if (editingCompany.value) {
      await adminStore.updateCompany(editingCompany.value.id, companyForm.value)
    } else {
      await adminStore.createCompany(companyForm.value)
    }
    
    closeModal()
    await loadCompanies()
    
  } catch (error) {
    alert(`Error: ${error.message}`)
  } finally {
    formLoading.value = false
  }
}

async function toggleCompanyStatus(company) {
  if (formLoading.value) return
  
  formLoading.value = true
  
  try {
    await adminStore.updateCompany(company.id, {
      ...company,
      is_active: !company.is_active
    })
    
    await loadCompanies()
    
  } catch (error) {
    alert(`Error: ${error.message}`)
  } finally {
    formLoading.value = false
  }
}

function deleteCompany(company) {
  deletingCompany.value = company
}

async function confirmDelete() {
  if (formLoading.value || !deletingCompany.value) return
  
  formLoading.value = true
  
  try {
    await adminStore.deleteCompany(deletingCompany.value.id)
    deletingCompany.value = null
    await loadCompanies()
    
  } catch (error) {
    alert(`Error: ${error.message}`)
  } finally {
    formLoading.value = false
  }
}

async function loadCompanies() {
  loading.value = true
  try {
    await adminStore.loadCompanies()
  } catch (error) {
    console.error('Failed to load companies:', error)
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(async () => {
  // Check authentication
  if (!await adminStore.checkSession()) {
    router.push('/saas/login')
    return
  }
  
  await loadCompanies()
})
</script>

<style scoped>
.company-manager {
  min-height: 100vh;
  background: #f7fafc;
}

.manager-header {
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 1rem 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left h1 {
  margin: 0;
  color: #2d3748;
  font-size: 1.75rem;
  font-weight: bold;
}

.header-left p {
  margin: 0.25rem 0 0 0;
  color: #718096;
  font-size: 0.9rem;
}

.header-right {
  display: flex;
  gap: 1rem;
}

.create-btn {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
}

.back-btn {
  padding: 0.75rem 1.5rem;
  background: #e2e8f0;
  color: #4a5568;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #cbd5e0;
  transform: translateY(-1px);
}

.filters-section {
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 1rem 2rem;
}

.filters-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.search-input {
  padding: 0.5rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  width: 300px;
  font-size: 0.9rem;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
}

.filter-select {
  padding: 0.5rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  cursor: pointer;
}

.stats-group {
  margin-left: auto;
  display: flex;
  gap: 1rem;
}

.stat-item {
  padding: 0.25rem 0.75rem;
  background: #edf2f7;
  border-radius: 20px;
  font-size: 0.85rem;
  color: #4a5568;
  font-weight: 500;
}

.companies-main {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #718096;
}

.empty-content h3 {
  color: #2d3748;
  margin-bottom: 1rem;
}

.companies-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
}

.company-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.company-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.company-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.company-title h3 {
  margin: 0 0 0.5rem 0;
  color: #2d3748;
  font-size: 1.25rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.status-badge.active {
  background: #c6f6d5;
  color: #22543d;
}

.status-badge.inactive {
  background: #fed7d7;
  color: #742a2a;
}

.company-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  padding: 0.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.edit-btn {
  background: #bee3f8;
  color: #2b6cb0;
}

.toggle-btn {
  background: #d6f5d6;
  color: #38a169;
}

.delete-btn {
  background: #fed7d7;
  color: #e53e3e;
}

.action-btn:hover {
  transform: scale(1.1);
}

.company-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
}

.detail-label {
  font-weight: 500;
  color: #718096;
  font-size: 0.9rem;
}

.detail-value {
  color: #2d3748;
  font-size: 0.9rem;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
}

.company-footer {
  border-top: 1px solid #e2e8f0;
  padding-top: 1rem;
}

.company-stats {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.stat-chip {
  padding: 0.25rem 0.5rem;
  background: #edf2f7;
  border-radius: 12px;
  font-size: 0.75rem;
  color: #4a5568;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

.danger-modal {
  border-top: 4px solid #e53e3e;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h2 {
  margin: 0;
  color: #2d3748;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #718096;
}

.modal-form {
  padding: 1.5rem;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2d3748;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
}

.form-hint {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.8rem;
  color: #718096;
}

.form-section {
  margin: 1.5rem 0;
  padding: 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
}

.form-section h4 {
  margin: 0 0 1rem 0;
  color: #4a5568;
  font-size: 1rem;
  font-weight: 600;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
}

.form-group select {
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.9rem;
  background: white;
  transition: border-color 0.2s;
}

.form-group select:focus {
  outline: none;
  border-color: #667eea;
}

.billing-status {
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.billing-status.trial {
  background: #fef3cd;
  color: #92400e;
}

.billing-status.active {
  background: #d1fae5;
  color: #065f46;
}

.billing-status.suspended {
  background: #fee2e2;
  color: #991b1b;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-weight: normal !important;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.warning-text {
  color: #e53e3e;
  font-weight: 500;
  margin-top: 1rem;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.save-btn {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.cancel-btn {
  padding: 0.75rem 1.5rem;
  background: #e2e8f0;
  color: #4a5568;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.delete-btn {
  padding: 0.75rem 1.5rem;
  background: #e53e3e;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 768px) {
  .companies-main {
    padding: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    gap: 1rem;
  }
  
  .filters-content {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input {
    width: 100%;
  }
  
  .stats-group {
    margin-left: 0;
    justify-content: center;
  }
  
  .companies-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-overlay {
    padding: 1rem;
  }
}
</style>